import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getServiceRequest, updateServiceRequest } from '../api/admin.js';
import type {
  ServiceRequestResolution,
  ServiceRequestState,
} from '@m8-app/contracts/rest/admin.js';

const STATES: ServiceRequestState[] = [
  'open', 'in_review', 'repair_scheduled', 'replace_scheduled',
  'resolved', 'rejected', 'closed',
];

export function ServiceDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['serviceRequest', id],
    queryFn: () => getServiceRequest(id),
    enabled: !!id,
  });

  const [resolveOpen, setResolveOpen] = useState<ServiceRequestResolution | null>(null);

  const { mutate: setState } = useMutation({
    mutationFn: (state: ServiceRequestState) => updateServiceRequest(id, { state }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['serviceRequest', id] }),
  });

  const { mutate: setRemark } = useMutation({
    mutationFn: (remark: string) => updateServiceRequest(id, { remark }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['serviceRequest', id] }),
  });

  if (isLoading || !data) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  const r = data.request;
  const d = data.device;

  return (
    <div className="p-8">
      <Link to="/services" className="text-sm text-indigo-600 hover:underline">← Service Requests</Link>
      <h2 className="text-2xl font-bold text-gray-900 mt-2">Service Request</h2>
      <p className="text-sm text-gray-500 font-mono">{r.id}</p>

      <div className="grid grid-cols-3 gap-6 mt-6">
        {/* Left: request */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3 text-sm">
            <h3 className="font-semibold text-gray-900">Request</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field k="Raised by" v={r.raisedBy} />
              <Field k="Category"  v={r.category} />
              <Field k="Created"   v={r.createdAt.slice(0, 19).replace('T', ' ')} />
              <Field k="Updated"   v={r.updatedAt.slice(0, 19).replace('T', ' ')} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Description</p>
              <p className="mt-1">{r.description}</p>
            </div>
            <div className="flex items-center gap-3">
              <label htmlFor="sr-state" className="text-xs text-gray-500">State</label>
              <select
                id="sr-state"
                value={r.state}
                onChange={(e) => setState(e.target.value as ServiceRequestState)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {r.resolution && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                  {r.resolution}{r.resolutionReason ? ` — ${r.resolutionReason}` : ''}
                </span>
              )}
            </div>
            <RemarkEditor initial={r.remark ?? ''} onSave={setRemark} />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Resolve</h3>
            <p className="text-xs text-gray-500 mb-3">Choose how this ticket was resolved. A reason is required.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setResolveOpen('repair')}
                disabled={!!r.resolution}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                Mark Repair
              </button>
              <button
                onClick={() => setResolveOpen('replace')}
                disabled={!!r.resolution}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Mark Replace
              </button>
              <button
                onClick={() => setResolveOpen('no_action')}
                disabled={!!r.resolution}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                No Action
              </button>
            </div>
          </div>
        </div>

        {/* Right: device + history */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 text-sm space-y-2">
            <h3 className="font-semibold text-gray-900 mb-2">Device</h3>
            <Field k="Serial"   v={d.serialNumber} />
            <Field k="Type"     v={d.typeName ?? '—'} />
            <Field k="Owner"    v={d.ownerMobile ?? '—'} />
            <Field k="Claimed"    v={d.claimStatus === 'claimed' ? 'Claimed' : 'Unclaimed'} />
            <Field k="Registered" v={d.sellerRegistered ? 'Registered' : 'Not Registered'} />
            <Field k="Presence" v={d.presence ?? '—'} />
            <Field k="Firmware" v={d.activeFirmwareVersion ?? '—'} />
            <Link to={`/devices/${d.id}`} className="block mt-2 text-xs text-indigo-600 hover:underline">Open device →</Link>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 text-sm">
            <h3 className="font-semibold text-gray-900 mb-2">Past Warranty Claims ({data.history.warrantyClaims.length})</h3>
            <ul className="space-y-1 max-h-40 overflow-auto">
              {data.history.warrantyClaims.map((w) => (
                <li key={w.id} className="text-xs text-gray-600 border-b border-gray-100 pb-1">
                  <span className="font-mono">{w.createdAt.slice(0, 10)}</span> · {w.state} — {w.description?.slice(0, 60) || '—'}
                </li>
              ))}
              {data.history.warrantyClaims.length === 0 && <p className="text-xs text-gray-400 italic">None</p>}
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 text-sm">
            <h3 className="font-semibold text-gray-900 mb-2">Past Service Requests ({data.history.pastServiceRequests.length})</h3>
            <ul className="space-y-1 max-h-40 overflow-auto">
              {data.history.pastServiceRequests.map((s) => (
                <li key={s.id} className="text-xs text-gray-600 border-b border-gray-100 pb-1">
                  <span className="font-mono">{s.createdAt.slice(0, 10)}</span> · {s.state}
                  {s.resolution && <> → <span className="font-medium">{s.resolution}</span></>}
                  {' '}— {s.description?.slice(0, 60) || '—'}
                </li>
              ))}
              {data.history.pastServiceRequests.length === 0 && <p className="text-xs text-gray-400 italic">None</p>}
            </ul>
          </div>
        </div>
      </div>

      {resolveOpen && (
        <ResolveDialog
          id={id}
          resolution={resolveOpen}
          onClose={() => setResolveOpen(null)}
        />
      )}
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-gray-500">{k}</span>
      <span className="text-gray-900 font-mono text-right truncate ml-2">{v}</span>
    </div>
  );
}

function RemarkEditor({ initial, onSave }: { initial: string; onSave: (s: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);

  if (!editing) {
    return (
      <div>
        <p className="text-xs text-gray-500">Remark</p>
        <button onClick={() => setEditing(true)} className="text-sm text-left text-gray-700 hover:text-indigo-600 w-full">
          {initial || <span className="italic text-gray-400">add remark</span>}
        </button>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">Remark</p>
      <div className="flex gap-2">
        <input value={value} onChange={(e) => setValue(e.target.value)} className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"/>
        <button onClick={() => { onSave(value); setEditing(false); }} className="text-xs px-3 bg-indigo-600 text-white rounded">Save</button>
        <button onClick={() => { setEditing(false); setValue(initial); }} className="text-xs px-3 text-gray-500">Cancel</button>
      </div>
    </div>
  );
}

function ResolveDialog({
  id, resolution, onClose,
}: {
  id: string;
  resolution: ServiceRequestResolution;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const { mutate, isPending, error } = useMutation({
    mutationFn: () => updateServiceRequest(id, {
      resolution,
      resolutionReason: reason,
      state: resolution === 'repair'
        ? 'repair_scheduled'
        : resolution === 'replace'
          ? 'replace_scheduled'
          : 'resolved',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['serviceRequest', id] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-1">Mark as {resolution}</h3>
        <p className="text-xs text-gray-500 mb-3">A reason is required and will be logged in the audit trail.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="Reason / notes"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          required
        />
        {error && <p className="text-sm text-red-600 mt-2">{(error as { message?: string }).message ?? 'Failed'}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => mutate()}
            disabled={isPending || reason.trim().length === 0}
            className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
